import type { HtmlToTextOptions } from "html-to-text"
import { convert } from "html-to-text"

const convertOptions: HtmlToTextOptions = {
	selectors: [
		{ selector: "img", format: "skip" },
		{ selector: "a", options: { ignoreHref: true } },
		{ selector: "h1", options: { uppercase: false } },
		{ selector: "h2", options: { uppercase: false } },
		{ selector: "h3", options: { uppercase: false } },
		{ selector: "h4", options: { uppercase: false } },
		{ selector: "b", format: "inline" },
		{ selector: "strong", format: "inline" },
		{ selector: "div", options: { trailingLineBreaks: 2 } },
		{ selector: "p", options: { trailingLineBreaks: 2 } },
	],
	preserveNewlines: true,
}

const convertOptionsWithLinks: HtmlToTextOptions = {
	...convertOptions,
	selectors: convertOptions.selectors?.map((s) =>
		"selector" in s && s.selector === "a"
			? {
					selector: "a" as const,
					options: { ignoreHref: false, hideLinkHrefIfSameAsText: true },
				}
			: s,
	),
}

function removeEmptyParagraphs(html: string) {
	return html.replace(/<p>\s*<\/p>/g, "")
}

export function convertHtmlToText(
	html: string,
	removeEmptyP?: boolean,
	options?: { preserveLinks?: boolean },
) {
	return convert(
		removeEmptyP ? removeEmptyParagraphs(html) : html,
		options?.preserveLinks ? convertOptionsWithLinks : convertOptions,
	)
}

// RFC 3676 wants the delimiter line to be exactly "-- ", but html-to-text
// trims the trailing space and turns the HTML &nbsp; into U+00A0.
export function restoreSignatureDelimiter(text: string) {
	return text.replace(/^--[ \u00A0]*$/gm, "-- ")
}

// The text/plain alternative of an outbound mail. A blank line ships as a <br>
// on top of the paragraph's own break, so the doubled runs collapse back to one.
export function convertOutboundHtmlToText(html: string) {
	return restoreSignatureDelimiter(convertHtmlToText(html)).replace(
		/\n{3,}/g,
		"\n\n",
	)
}

// The expression below matches timestamps and formats them as bulletpoints, with the timestamp in parentheses.
// Try it out: https://regex101.com/r/aAtX2f/1
export function convertTimestampsToBulletpoints(html: string) {
	const convertedHtml = html.replace(
		/<timestamp([^>]*)data-timestamp-time="([^"]*)"(.*?)>(.*?)<\/timestamp>/g,
		"<ul><li>($2): $4</li></ul>",
	)
	return convertedHtml
}

export function removeAllBrTags(html: string) {
	return html.replace(/<br\s*\/?>/gi, "")
}
